import type { ColDef } from "ag-grid-community";
import { AlertCircleIcon, Building2Icon, RefreshCwIcon } from "lucide-react";
import React, { useMemo, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { LoadingPage } from "@/components/LoadingPage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Text } from "@/components/ui/text";
import {
  AdminGrid,
  denseAdminGridTheme,
} from "@/features/admin/components/AdminGrid";
import { ITransplantOrgConfigDialog } from "./ITransplantOrgConfigDialog";
import { type OrgRow, readOrgUrl, readTokenSet } from "./org-row";

type OrgScope = "active" | "all";

function hasOrgConfig(config: Record<string, unknown> | undefined): boolean {
  return !!readOrgUrl(config) || readTokenSet(config);
}

function compareOrgs(a: OrgRow, b: OrgRow): number {
  // Enabled senders first, then configured-but-disabled, then name.
  if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
  const aConfigured = hasOrgConfig(a.config);
  const bConfigured = hasOrgConfig(b.config);
  if (aConfigured !== bConfigured) return aConfigured ? -1 : 1;
  return a.name.localeCompare(b.name);
}

export function ITransplantOrgsPanel() {
  const { data, isLoading, isError, error, isFetching, refetch } =
    trpc.integrations.listOrgs.useQuery({
      kind: "ITRANSPLANT",
    });

  const [editing, setEditing] = useState<OrgRow | null>(null);
  const [scope, setScope] = useState<OrgScope>("active");
  const [search, setSearch] = useState("");

  const columnDefs = React.useMemo<ColDef<OrgRow>[]>(
    () => [
      {
        field: "name",
        headerName: "Organization",
        flex: 2,
        minWidth: 180,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "orgType",
        headerName: "Type",
        minWidth: 100,
        maxWidth: 140,
        filter: "agSetColumnFilter",
        cellRenderer: OrgTypeBadge,
      },
      {
        colId: "url",
        headerName: "API URL",
        flex: 2,
        minWidth: 200,
        valueGetter: ({ data: row }) => readOrgUrl(row?.config) || "—",
        tooltipValueGetter: ({ data: row }) =>
          readOrgUrl(row?.config) || undefined,
        filter: "agTextColumnFilter",
        cellClass: "font-mono text-xs",
      },
      {
        colId: "token",
        headerName: "Token",
        minWidth: 90,
        maxWidth: 110,
        valueGetter: ({ data: row }) =>
          readTokenSet(row?.config) ? "Set" : "—",
        filter: "agSetColumnFilter",
        filterParams: { values: ["Set", "—"] },
      },
      {
        colId: "status",
        headerName: "Status",
        minWidth: 110,
        maxWidth: 130,
        valueGetter: ({ data: row }) => (row?.enabled ? "Enabled" : "Disabled"),
        filter: "agSetColumnFilter",
        filterParams: { values: ["Enabled", "Disabled"] },
        cellRenderer: StatusBadge,
      },
      {
        colId: "actions",
        headerName: "",
        minWidth: 110,
        maxWidth: 130,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        cellStyle: { padding: 0 },
        cellRenderer: ({ data: row }: { data?: OrgRow }) => {
          if (!row) return null;
          const label =
            row.enabled || hasOrgConfig(row.config) ? "Edit" : "Configure";
          return (
            <div className="flex h-full items-center justify-end pe-2">
              <Button
                size="sm"
                variant="outline"
                onPress={() => setEditing(row)}
              >
                {label}
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const enabledCount = useMemo(
    () => (data ?? []).filter((o) => o.enabled).length,
    [data],
  );
  const configuredCount = useMemo(
    () => (data ?? []).filter((o) => hasOrgConfig(o.config)).length,
    [data],
  );

  const rowData = useMemo(() => {
    if (!data) return [];
    const filtered =
      scope === "active"
        ? data.filter((o) => o.enabled || hasOrgConfig(o.config))
        : data;
    return [...filtered].sort(compareOrgs);
  }, [data, scope]);

  if (isLoading) return <LoadingPage />;

  if (isError || !data) {
    return (
      <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-950/20">
            <div className="flex items-start gap-3">
              <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
              <div className="flex flex-col gap-1">
                <Text
                  size="2"
                  className="font-medium text-red-800 dark:text-red-200"
                >
                  Couldn't load organizations
                </Text>
                <Text size="1" muted>
                  {error?.message ?? "Unknown error"}
                </Text>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              isPending={isFetching}
              onPress={() => refetch()}
            >
              <RefreshCwIcon /> Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1.5">
            <CardTitle>Organizations</CardTitle>
            <CardDescription>
              Send credentials per org: API URL and token. Enable only when both
              are set. Case type is set on each accession.
            </CardDescription>
          </div>
          <Badge
            color={enabledCount > 0 ? "green" : "gray"}
            className="shrink-0"
          >
            {enabledCount} enabled
            {configuredCount > enabledCount
              ? ` · ${configuredCount} configured`
              : null}
          </Badge>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant={scope === "active" ? "default" : "outline"}
              onPress={() => setScope("active")}
            >
              Active
            </Button>
            <Button
              size="sm"
              variant={scope === "all" ? "default" : "outline"}
              onPress={() => setScope("all")}
            >
              All orgs
            </Button>
          </div>
          <SearchField
            aria-label="Search organizations"
            value={search}
            onChange={setSearch}
            className="w-full max-w-xs"
          >
            <SearchInput placeholder="Search by name…" />
          </SearchField>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 min-h-0 p-0! border-t">
        {scope === "active" && rowData.length === 0 ? (
          <EmptyState className="h-full w-full flex items-center justify-center">
            <EmptyStateIcon>
              <Building2Icon />
            </EmptyStateIcon>
            <EmptyStateHeading>
              No active or configured organizations yet
            </EmptyStateHeading>
            <EmptyStateDescription>
              Switch to All orgs to pick a client and set its API URL and token.
            </EmptyStateDescription>
            <EmptyStateActions>
              <Button
                size="sm"
                variant="outline"
                onPress={() => setScope("all")}
              >
                Show all orgs
              </Button>
            </EmptyStateActions>
          </EmptyState>
        ) : (
          <AdminGrid<OrgRow>
            theme={denseAdminGridTheme}
            rowData={rowData}
            columnDefs={columnDefs}
            quickFilterText={search}
            getRowId={({ data: row }) => String(row.orgId)}
            defaultColDef={{
              flex: 1,
              resizable: true,
              sortable: false,
              enableRowGroup: false,
              enablePivot: false,
              enableValue: false,
              filterParams: { maxNumConditions: 1 },
            }}
            suppressDragLeaveHidesColumns
            sideBar={{ toolPanels: ["filters"] }}
            tooltipShowDelay={400}
          />
        )}
      </CardContent>
      <ITransplantOrgConfigDialog
        org={editing}
        onClose={() => setEditing(null)}
      />
    </Card>
  );
}

function OrgTypeBadge({ value }: { value?: string }) {
  if (!value) return null;
  return (
    <div className="flex h-full items-center">
      <Badge color="gray" size="sm">
        {value}
      </Badge>
    </div>
  );
}

function StatusBadge({ value }: { value?: string }) {
  const enabled = value === "Enabled";
  return (
    <div className="flex h-full items-center">
      <Badge color={enabled ? "green" : "gray"} size="sm">
        {value}
      </Badge>
    </div>
  );
}
